import { useState } from "react";
import { CheckCircle2, Headphones, Lock, Minus, PlayCircle, Plus } from "lucide-react";
import { curriculumPreviewModules } from "../data/curriculumPreview";

const getItemId = (item, fallback) =>
  item?.id ||
  item?._id ||
  item?.vimeoProjectId ||
  item?.vimeoVideoId ||
  item?.vimeoProjectUri ||
  item?.vimeoVideoUri ||
  fallback;

const getDurationLabel = (duration) => {
  if (!duration) return null;
  if (typeof duration === "string") return duration;

  const minutes = Math.max(1, Math.round(Number(duration) / 60));
  return `${minutes} min`;
};

const getLessonCount = (item) =>
  (item.lessons || []).length +
  (item.subtopics || []).reduce(
    (total, subtopic) => total + getLessonCount(subtopic),
    0
  );

const LessonList = ({ lessons = [] }) => (
  <ul className="space-y-2">
    {lessons.map((lesson, index) => (
      <li
        key={getItemId(lesson, `lesson-${index}`)}
        className="flex items-start gap-3 rounded-lg border border-white/[0.06] bg-white/[0.025] px-4 py-3"
      >
        <PlayCircle
          className="mt-0.5 w-4 h-4 flex-shrink-0 text-[#C5A059]"
          strokeWidth={1.8}
        />
        <div className="min-w-0">
          <p className="text-sm font-medium text-zinc-200">{lesson.title}</p>
          {getDurationLabel(lesson.duration) && (
            <p className="mt-1 text-xs text-zinc-500">
              {getDurationLabel(lesson.duration)}
            </p>
          )}
        </div>
      </li>
    ))}
  </ul>
);

const SubtopicList = ({ subtopics = [] }) => (
  <div className="space-y-4">
    {subtopics.map((subtopic, index) => (
      <div
        key={getItemId(subtopic, `subtopic-${index}`)}
        className="rounded-xl border border-white/[0.06] bg-[#0A0A0C] p-4 md:p-5"
      >
        <div className="flex items-center justify-between gap-4">
          <h4 className="font-heading text-base font-medium text-white">
            {subtopic.title}
          </h4>
          <span className="text-xs text-zinc-500">
            {getLessonCount(subtopic)} videos
          </span>
        </div>

        {subtopic.lessons?.length > 0 && (
          <div className="mt-4">
            <LessonList lessons={subtopic.lessons} />
          </div>
        )}

        {subtopic.subtopics?.length > 0 && (
          <div className="mt-4 border-l border-white/[0.08] pl-4">
            <SubtopicList subtopics={subtopic.subtopics} />
          </div>
        )}
      </div>
    ))}
  </div>
);

export const CourseAccordion = ({
  onUnlock,
  onEnterStudio,
  isPaying = false,
  hasActiveEntitlement = false,
  isEntitlementLoading = false,
}) => {
  const [openId, setOpenId] = useState(
    getItemId(curriculumPreviewModules[0], "module-0")
  );
  const ctaDisabled = isPaying || isEntitlementLoading;

  return (
    <section
      id="curriculum"
      data-testid="curriculum-section"
      className="relative py-24 md:py-32 px-6 md:px-12 lg:px-16 max-w-7xl mx-auto"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        {/* Left header */}
        <div className="lg:col-span-4 lg:sticky lg:top-32 self-start">
          <div className="text-xs uppercase tracking-[0.25em] text-[#C5A059] font-semibold mb-5">
            What you&apos;ll learn
          </div>
          <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tighter text-white leading-[1.05]">
            Seven Chapters.
            <br />
            <span className="gold-text italic font-light">One craft.</span>
          </h2>
          <p className="mt-6 text-zinc-400 text-base leading-relaxed max-w-md">
            {hasActiveEntitlement
              ? "Every module is available from your studio. Continue into the protected lessons whenever you are ready."
              : "Every module unlocks a new layer of the vocal chain. Subscribe to access the full session files and walkthroughs."}
          </p>
          <div className="mt-8 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10">
            {hasActiveEntitlement ? (
              <CheckCircle2 className="w-3 h-3 text-[#C5A059]" strokeWidth={1.8} />
            ) : (
              <Lock className="w-3 h-3 text-[#C5A059]" strokeWidth={1.8} />
            )}
            <span className="text-xs text-zinc-400">
              {hasActiveEntitlement ? "Access granted" : "Preview locked"}
            </span>
          </div>
        </div>

        {/* Accordion */}
        <div className="lg:col-span-8 space-y-3">
          {curriculumPreviewModules.map((topic, idx) => {
            const topicId = getItemId(topic, `module-${idx}`);
            const isOpen = openId === topicId;
            const lessonCount = getLessonCount(topic);
            const durationLabel = getDurationLabel(topic.duration);

            return (
              <div
                key={topicId}
                data-testid={`topic-${topicId}`}
                className="relative overflow-hidden rounded-2xl border border-white/[0.08] bg-[#0D0D0F] hover:border-white/[0.14] transition-colors"
              >
                <button
                  data-testid={`topic-toggle-${topicId}`}
                  onClick={() => setOpenId(isOpen ? null : topicId)}
                  className="w-full flex items-center justify-between gap-6 p-6 md:p-7 text-left group"
                >
                  <div className="flex items-center gap-5 min-w-0">
                    <span className="font-heading text-sm text-[#C5A059] font-medium tracking-wider w-8">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <div className="min-w-0">
                      <div className="font-heading text-lg md:text-xl text-white font-medium truncate">
                        {topic.title}
                      </div>
                      <div className="text-xs text-zinc-500 mt-1 flex items-center gap-3">
                        <span>{lessonCount} videos</span>
                        {durationLabel && (
                          <>
                            <span className="w-1 h-1 rounded-full bg-zinc-700" />
                            <span>{durationLabel}</span>
                          </>
                        )}
                        <span className="flex items-center gap-1">
                          {hasActiveEntitlement ? (
                            <CheckCircle2 className="w-3 h-3" strokeWidth={2} />
                          ) : (
                            <Lock className="w-3 h-3" strokeWidth={2} />
                          )}
                          {hasActiveEntitlement ? "Owned" : "Locked"}
                        </span>
                      </div>
                    </div>
                  </div>
                  <span className="flex-shrink-0 w-9 h-9 rounded-full border border-white/10 flex items-center justify-center text-zinc-400 group-hover:border-[#C5A059]/50 group-hover:text-[#C5A059] transition-all">
                    {isOpen ? (
                      <Minus className="w-4 h-4" strokeWidth={1.8} />
                    ) : (
                      <Plus className="w-4 h-4" strokeWidth={1.8} />
                    )}
                  </span>
                </button>

                {isOpen && (
                  <div
                    data-testid={`topic-content-${topicId}`}
                    className="relative px-6 md:px-7 pb-7 pt-0"
                  >
                    <div className="rounded-xl border border-white/[0.06] bg-[#0A0A0C] p-4 md:p-5">
                      {topic.subtopics?.length > 0 ? (
                        <SubtopicList subtopics={topic.subtopics} />
                      ) : topic.lessons?.length > 0 ? (
                        <LessonList lessons={topic.lessons} />
                      ) : (
                        <p className="text-sm text-zinc-500">
                          Module contents will be available soon.
                        </p>
                      )}

                      <div className="mt-5 flex justify-start border-t border-white/[0.06] pt-5">
                        <button
                          data-testid={`topic-unlock-${topicId}`}
                          onClick={hasActiveEntitlement ? onEnterStudio : onUnlock}
                          disabled={ctaDisabled}
                          className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full bg-white/[0.06] border border-white/15 text-sm font-medium text-white hover:bg-[#C5A059]/15 hover:border-[#C5A059]/40 disabled:cursor-not-allowed disabled:opacity-70 transition-all duration-300"
                        >
                          {hasActiveEntitlement ? (
                            <Headphones
                              className="w-3.5 h-3.5 text-[#C5A059]"
                              strokeWidth={2}
                            />
                          ) : (
                            <Lock
                              className="w-3.5 h-3.5 text-[#C5A059]"
                              strokeWidth={2}
                            />
                          )}
                          {hasActiveEntitlement
                            ? "Enter Studio"
                            : isEntitlementLoading
                              ? "Checking access..."
                              : isPaying
                                ? "Opening checkout..."
                                : "Unlock videos"}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
